import {NativeBackService} from "../../_services/native-back.service";
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    inject,
    OnDestroy,
    Output,
    SecurityContext,
    ViewChild
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {ContentService} from '../../_services/content.service';
import {CommunitiesService} from '../../_services/communities.service';
import {Category, Community, SubGroup, UserAccessLevel} from '../../_types/communities';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {combineLatest, distinctUntilChanged, filter, map, Observable, startWith, take} from 'rxjs';
import {removeNullUndefinedFields} from '../../_utils/remove-null-fields';
import {
    FeedPost,
    ImageFile,
    NewPost,
    Post,
    PostAttachment,
    PostAttachmentType,
    SimplifiedPost,
    UpdatePost
} from '../../_types/content';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {LocalStorageService} from '../../_services/local-storage.service';
import {Heatmap, Poll} from '../../_types/polls';
import {PollsService} from '../../_services/polls.service';
import {SnackbarService} from '../../_services/snackbar.service';
import {AuthService} from "../../_services/auth.service";
import {localDateShift, mergeDateAndTime, parseISODate} from '../../_utils/date';
import {ActivatedRoute, Router} from "@angular/router";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {AttachmentsComponent} from '../attachments/attachments.component';

import {TitleCasePipe} from "@angular/common";
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";
import {ConfirmationDialogData} from "../../_types/shared";
import {
    Alignment,
    AutoLink,
    Bold,
    ClassicEditor,
    ContextualBalloon,
    Emoji,
    EmojiMention,
    Essentials,
    FileLoader,
    FileRepository,
    Font,
    FontBackgroundColor,
    FontColor,
    FontSize,
    Fullscreen,
    Heading,
    HorizontalLine,
    ImageBlock,
    ImageCaption,
    ImageEditing,
    ImageInline,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    ImageUtils,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    Mention,
    Paragraph,
    SpecialCharacters,
    SpecialCharactersEssentials,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    UploadAdapter
} from 'ckeditor5';
import AiAssistantPlugin from "../../_utils/ai-assistant.plugin";
import {throttleTime} from "rxjs/operators";

import {DirectUploadService, PostUploadHandle} from '../../_services/direct-upload.service';

type AttachmentSlot = `image:${number}` | 'media' | 'document' | 'audio' | 'url' | 'survey';
type CompressFn = (file: File) => Promise<File | Blob>;

interface CreatePostDialogData {
    post?: Post;
    poll?: Poll;
    subgroup?: string | number | null;
}


export class Base64CompressUploadAdapter implements UploadAdapter {
    constructor(
        private loader: FileLoader,
        private compress: CompressFn
    ) {
    }

    async upload(): Promise<{ default: string }> {
        const compressableImageTypes: string[] = ['bmp', 'webp', 'png', 'jpeg', 'jpg']

        const file: File | null = await this.loader.file;
        if (!file) {
            throw new Error('No file to upload.');
        }

        const ext: string = file.name.split('.').pop()?.toLowerCase() ?? '';

        if (!compressableImageTypes.includes(ext)) {
            throw new Error(`Only these image types are allowed: bmp, webp, png, jpeg, jpg`);
        }

        const compressed: File | Blob = await this.compress(file);

        const dataUrl: string = await blobToDataURL(compressed);

        return {default: dataUrl};
    }
}

function blobToDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
    });
}

@Component({
    selector: 'app-create-post',
    templateUrl: './create-post.component.html',
    styleUrl: './create-post.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreatePostComponent implements OnDestroy {
    @ViewChild('attachmentComponentRef', {static: false}) attachmentComponent?: AttachmentsComponent;
    @ViewChild('ckHost', {read: ElementRef}) set ckHost(ref: ElementRef<HTMLElement> | undefined) {
        this.ckHostRef = ref;
        if (ref && !this.editorReady && !this.useBasicEditor && !this.editorStartupTimer) {
            this.editorStartupTimer = setTimeout(() => {
                this.onEditorError(new Error('Discussion editor startup timed out.'));
            }, 10000);
        }
    }
    @Output() removedPollOrHeatmap: EventEmitter<'poll' | 'heatmap'> = new EventEmitter<'poll' | 'heatmap'>()
    @Output() updatedPoll: EventEmitter<void> = new EventEmitter<void>();
    attachmentsValid: boolean = true;
    pollSurveyValid: boolean = true;
    docValid: boolean = true;
    urlValid: boolean = true;
    imageValid: boolean = true;
    audioValid: boolean = true;
    createPostForm: FormGroup;
    lockPostControl: FormControl = this.formBuilder.control(false);
    dateControl: FormControl = this.formBuilder.control({value: new Date(), disabled: true});
    timeControl: FormControl = this.formBuilder.control({value: '00:00', disabled: true});
    revealDateControl: FormControl = this.formBuilder.control({value: null, disabled: false});
    revealTimeControl: FormControl = this.formBuilder.control({value: '00:00', disabled: false});
    attachment: any;
    document: any;
    url: any;
    images: any;
    audio: any;
    survey: any;
    poll: any;
    attachmentType: string = 'text';
    attachmentInitial: boolean = false;
    communities: Community[] = [];
    subgroups: SubGroup[] = [];
    categories: Category[] = [];
    isLoaded: boolean = false;
    editPost?: Post;
    addPoll: boolean = false;
    sending: boolean = false;
    emojiEnabled: boolean = false;
    editPoll?: Poll;
    defaultAttachments: string[] = ['text', 'video', 'videoLink', 'externalLink', 'screenRecording', 'gif', 'heatmap']
    pollAttachments: string[] = ['text', 'survey_url', 'poll'];
    editPollId?: number;
    community: Community | null = null
    editorInstance: any;
    editorReady = false;
    useBasicEditor = false;
    basicEditorHtml = '';
    currentSubgroups: SubGroup[] = [];
    filteredSubgroups: Observable<string[]> | any;
    subgroupSearchControl: FormControl = new FormControl();
    public Editor: typeof ClassicEditor = ClassicEditor;
    public config = {
        aiAssistant: {
            contentService: this.contentService,
            snackbarService: this.snackbarService,
            communitiesService: this.communitiesService
        },
        extraPlugins: [AiAssistantPlugin],
        plugins: [Link, AutoLink, Essentials, Bold, Font, Italic, Paragraph, List, Heading, Subscript, Superscript, HorizontalLine,
            Fullscreen, Emoji, EmojiMention, Mention, Alignment, Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties, TableToolbar,
            ImageBlock, ImageCaption, ImageEditing, ImageInline, ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar,
            ImageUpload, ImageUtils, FontBackgroundColor, FontColor, FontSize, Indent, IndentBlock, SpecialCharacters, SpecialCharactersEssentials, ContextualBalloon,],
        toolbar: {
            items: [
                'aiAssistant',
                'undo', 'redo', '|',
                'link', 'unlink', '|',
                'bold', 'italic', 'heading', 'alignment', 'fontSize', 'fontColor', 'fontBackgroundColor', 'strikethrough', 'subscript', 'superscript', '|',
                'insertTable', '|',
                'horizontalLine', '|',
                'numberedList', 'bulletedList', 'outdent', 'indent', 'todoList', '|',
                'uploadImage', '|',
                'emoji', 'specialCharacters', '|',
            ],
            shouldNotGroupWhenFull: false
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
        },
        image: {
            toolbar: [
                'toggleImageCaption',
                'imageTextAlternative',
                '|',
                'imageStyle:inline',
                'imageStyle:wrapText',
                'imageStyle:breakText',
                '|',
                'resizeImage'
            ]
        },
        // Keep editor startup independent of access to CKEditor's external CDN.
        emoji: {
            definitionsUrl: new URL('assets/ckeditor/emoji-en-v16.json', document.baseURI).href
        },
        licenseKey: 'GPL'
    }

    filteredPosts: SimplifiedPost[] = [];
    postSearchControl: FormControl = new FormControl();
    postSearchLoading: boolean = false;

    docInUse: boolean = false;
    audioInUse: boolean = false;
    urlInUse: boolean = false;
    imageInUse: boolean = false;
    surveyPollInUse: boolean = false;
    multiAttachmentInUse: boolean = false;
    private readonly directUpload = inject(DirectUploadService);
    private readonly selection = new Map<AttachmentSlot, { type: string; value: any; initial?: boolean }>();
    private readonly uploads = new Map<AttachmentSlot, { file: File; community: number; handle: PostUploadHandle }>();
    private imagesChanged = false;
    private retainedImageIds: number[] = [];
    private initialImagesPost?: Post;
    private cachedInitialImages: ImageFile[] | null = null;
    private destroyed = false;
    private keepAlive = false;

    private postUploadLoaded = 0;
    private postUploadTotal = 0;
    private postUploadUnknown = false;
    private savingPost = false;

    get uploadProgress(): number | null {
        if (this.savingPost || this.postUploadUnknown) return null;
        const uploads = [...this.uploads.values()];
        const total = uploads.reduce((sum, item) => sum + item.file.size, this.postUploadTotal);
        if (!total) return null;
        const loaded = uploads.reduce((sum, item) => sum + item.file.size * item.handle.progress(), this.postUploadLoaded);
        // Do not display 100% until every byte has been sent.
        return Math.min(100, Math.floor(100 * loaded / total));
    }

    get uploadStatus(): string {
        if (this.postUploadUnknown && !this.savingPost) return 'Uploading attachments…';
        const progress = this.uploadProgress;
        return progress === null || progress === 100 ? 'Saving discussion…' : `Uploading attachments: ${progress}%`;
    }

    private readonly onPostUploadProgress = (loaded: number, total?: number): void => {
        this.postUploadUnknown = !total;
        this.postUploadLoaded = loaded;
        if (total) this.postUploadTotal = total;
    };

    private startKeepAlive(): void {
        if (!this.keepAlive) {
            this.authService.disableTimeout(4 * 60 * 60 * 1000);
            this.keepAlive = true;
        }
    }

    private stopKeepAlive(): void {
        this.authService.stopTimeoutDisable();
        this.keepAlive = false;
    }

    private selectAttachment(slot: AttachmentSlot, event: { type: string; value: any; initial?: boolean }): void {
        this.selection.set(slot, {...event});
        this.syncUpload(slot);
    }

    private syncUpload(slot: AttachmentSlot): void {
        const event = this.selection.get(slot);
        const file = event?.value;
        const existing = this.uploads.get(slot);
        const community = this.communityId;
        const needsUpload = file instanceof File && !event?.initial && !!community;
        if (existing && needsUpload && existing.file === file && existing.community === community) return;
        if (existing) {
            void existing.handle.abort();
            this.uploads.delete(slot);
        }
        if (needsUpload) {
            this.startKeepAlive();
            const handle = this.directUpload.uploadPost(file, community!);
            this.uploads.set(slot, {file, community: community!, handle});
            // Observe early failures even when the user has not pressed Save yet.
            void handle.done.catch(err => {
                if (!this.destroyed && this.uploads.get(slot)?.handle === handle && !this.sending) {
                    this.snackbarService.openErrorSnackBar(err instanceof Error ? err.message : String(err));
                }
            });
        }
        if (!this.uploads.size && !this.sending) this.stopKeepAlive();
    }

    private async prepareAttachments(postData: NewPost | UpdatePost): Promise<boolean> {
        this.postUploadLoaded = 0;
        this.postUploadTotal = 0;
        delete postData.images;
        this.postUploadUnknown = false;
        this.savingPost = false;
        const fields = {video_url: 'video_urls', gif_url: 'gif_urls', url: 'urls', survey_url: 'survey_urls'} as const;
        delete postData.attachment_files;
        for (const field of Object.values(fields)) delete postData[field];
        const removed: number[] = [];
        for (const slot of ['media', 'document', 'audio', 'url', 'survey'] as AttachmentSlot[]) {
            const event = this.selection.get(slot);
            if (!event) continue; // An editor still loading its initial value is unchanged.
            const type = event.type === 'videoLink' ? 'video_url' : event.type === 'gif' ? 'gif_url'
                : event.type === 'survey' ? 'survey_url' : event.type;
            const value = type === 'gif_url' ? event.value?.embed_url ?? event.value : event.value;
            const originals = (this.editPost?.attachments ?? []).filter(a => slot === 'media'
                ? ['video', 'video_url', 'gif_url'].includes(a.attachment_type)
                : a.attachment_type === (slot === 'survey' ? 'survey_url' : slot));
            const unchanged = originals.find(a => a.attachment_type === type && value &&
                (event.initial || (!(value instanceof File) && a.url === value)));
            removed.push(...originals.filter(a => a !== unchanged).map(a => a.id));
            if (!unchanged && value && type in fields) {
                postData[fields[type as keyof typeof fields]] = value;
            }
            this.syncUpload(slot);
        }
        if (this.imagesChanged) {
            const originals = [...(this.editPost?.images ?? []), ...(this.editPost?.attachments ?? []).filter(a => a.attachment_type === 'image')];
            removed.push(...originals.filter(a => !this.retainedImageIds.includes(a.id)).map(a => a.id));
        }
        for (const slot of this.selection.keys()) {
            if (slot.startsWith('image:')) this.syncUpload(slot);
        }
        if (removed.length) (postData as UpdatePost).attachments_to_delete = [...new Set(removed)].join(',');
        this.startKeepAlive();
        const pending = [...this.uploads.values()];
        try {
            const states = await Promise.all(pending.map(item => item.handle.done));
            if (this.destroyed) return false;
            if (pending.length !== this.uploads.size || pending.some(item => ![...this.uploads.values()].includes(item))) {
                throw new Error('Attachments changed during upload. Please save again.');
            }
            if (states.length) postData.direct_upload_ids = states.map(state => state.upload_id).join(',');
            return true;
        } catch (err) {
            this.sending = false;
            this.stopKeepAlive();
            if (!this.destroyed) this.snackbarService.openErrorSnackBar(err instanceof Error ? err.message : String(err));
            // Failed handles cannot be reused; selecting again or pressing Save starts a fresh upload.
            for (const [slot, item] of this.uploads) {
                if (pending.includes(item)) {
                    void item.handle.abort();
                    this.uploads.delete(slot);
                }
            }
            return false;
        }
    }

    private ckUiObserver?: MutationObserver;
    private ckHostRef?: ElementRef<HTMLElement>;
    private editorStartupTimer?: ReturnType<typeof setTimeout>;
    private readonly sanitizer = inject(DomSanitizer);
    private readonly nativeBack = inject(NativeBackService);
    private unregisterNativeBack: () => void = () => {};

    constructor(
        private readonly matDialog: MatDialogRef<CreatePostComponent>,
        private readonly dialog: MatDialog,
        private readonly formBuilder: FormBuilder,
        private readonly contentService: ContentService,
        private readonly communitiesService: CommunitiesService,
        private readonly localStorageService: LocalStorageService,
        private readonly pollsService: PollsService,
        private readonly destroyRef: DestroyRef,
        protected readonly authService: AuthService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly snackbarService: SnackbarService,
        @Inject(MAT_DIALOG_DATA) private data: CreatePostDialogData
    ) {
        this.unregisterNativeBack = this.nativeBack.register(this.matDialog.id, () => this.exitCheck());
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });
        this.createPostForm = this.formBuilder.group({
            title: this.formBuilder.control('', Validators.required),
            text: this.formBuilder.control('', Validators.required),
            vote_button_text: this.formBuilder.control('', Validators.maxLength(255)),
            attachment_button_text: this.formBuilder.control('', Validators.maxLength(255)),
            link_button_text: this.formBuilder.control('', Validators.maxLength(255)),
            community: this.formBuilder.control(null, Validators.required),
            subgroup: this.formBuilder.control(null),
            categories: this.formBuilder.control({value: null, disabled: true}),
            is_pinned: this.formBuilder.control(false),
            is_pinned_to_sidebar: this.formBuilder.control(false),
            hide_unmoderated_comments: this.formBuilder.control(false),
            is_closed_loop: this.formBuilder.control(false),
            show_attachment_above_text: this.formBuilder.control(false),
            is_anonymous: this.formBuilder.control(false),
            iframe_survey: this.formBuilder.control(false),
            original_post: this.formBuilder.control(null),
            lock_post_at: this.formBuilder.control({value: null, disabled: true}),
            off_comments_at_time: this.formBuilder.control({value: false, disabled: true}),
            off_likes_at_time: this.formBuilder.control({value: false, disabled: true}),
            off_dislikes_at_time: this.formBuilder.control({value: false, disabled: true}),
            is_hidden: this.formBuilder.control(false),
        });

        this.createPostForm.valueChanges.pipe(
            throttleTime(30 * 1000, undefined, {leading: true, trailing: true}),
            takeUntilDestroyed(),
        ).subscribe((): void => {
            // refresh user when activity on form
            void this.authService.refreshUser().catch(() => {});
        });

        this.createPostForm.get('community')?.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntilDestroyed()
        ).subscribe((id): void => {
            for (const slot of this.selection.keys()) this.syncUpload(slot);
            this.changeCommunity(id);
            this.loadSubgroups(id);
            this.getCategories(id, this.data?.post);
        });
        if (this.isSuperuser) {
            this.communitiesService.getAllCommunities().pipe(takeUntilDestroyed()).subscribe(data => {
                this.communities = data;
                if (this.lastSelectedCommunity) {
                    if (this.lastSelectedCommunity?.emoji_enabled && !this.config.toolbar.items.includes('emoji')) {
                        this.config.toolbar.items.push('|');
                        this.config.toolbar.items.push('emoji');
                    }
                    this.createPostForm.get('community')?.setValue(this.lastSelectedCommunity.id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                } else if (this.communities.length === 1) {
                    this.createPostForm.get('community')?.setValue(this.communities[0].id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                    this.changeCommunity(this.communities[0].id);
                }
                this.isLoaded = true;
                if (this.data?.post) {
                    this.editPost = this.data?.post;
                    this.editPoll = this.data?.poll;
                    this.editPollId = this.data?.poll?.id;
                    this.addPoll = !!this.data?.poll;

                    this.loadDataForEdit(this.editPost)
                }
            });

            this.pollAttachments.push('ai_comment_builder')
        } else {
            this.communitiesService.getUserCommunities().pipe(takeUntilDestroyed()).subscribe(data => {
                this.communities = data.map(result => result.community);
                if (this.lastSelectedCommunity) {
                    if (this.lastSelectedCommunity?.emoji_enabled && !this.config.toolbar.items.includes('emoji')) {
                        this.config.toolbar.items.push('|');
                        this.config.toolbar.items.push('emoji');
                    }
                    this.createPostForm.get('community')?.setValue(this.lastSelectedCommunity.id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                } else if (this.communities.length === 1) {
                    this.createPostForm.get('community')?.setValue(this.communities[0].id);
                    this.createPostForm.get('community')?.updateValueAndValidity();
                    this.changeCommunity(this.communities[0].id);
                }
                this.isLoaded = true;
                if (this.data?.post) {
                    this.editPost = this.data?.post;
                    this.editPoll = this.data?.poll;
                    this.editPollId = this.data?.poll?.id;
                    this.addPoll = !!this.data?.poll;

                    this.loadDataForEdit(this.editPost)
                }
            });
        }
        this.manageLockState();

        this.postSearchControl.valueChanges.subscribe(value => {
            this.postSearchLoading = true;
            let filterValue: string = '';
            if (typeof value == 'string') {
                filterValue = value.toLowerCase();
            }
            const filters = {
                search: filterValue,
                is_closed_loop: false,
                community_id: this.lastSelectedCommunity?.id!
            }
            this.contentService.getSimplifiedPosts(1, 200, filters).pipe(take(1))
                .subscribe({
                    next: result => {
                        this.filteredPosts = result.results;
                        this.postSearchLoading = false;
                    }, error: err => {
                        this.filteredPosts = [];
                        this.postSearchLoading = false;
                    }
                });
        })

        this.filteredSubgroups = this.subgroupSearchControl.valueChanges
            .pipe(
                startWith(''),
                map(value => this._filterSubgroups(value))
            );
    }

    get isClosedLoop(): boolean {
        return this.createPostForm.get('is_closed_loop')?.value || false
    }

    get communityId(): number | null {
        return this.createPostForm.get('community')?.getRawValue();
    }

    get lastSelectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    get textFormControl(): FormControl {
        return this.createPostForm?.get('text') as FormControl;
    }

    get initialDocument(): PostAttachment | null {
        let document: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'document') {
                    document = attachment;
                    this.docInUse = true;
                }
            }
        }
        return document;
    }

    get initialUrl(): PostAttachment | null {
        let URL: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'url') {
                    URL = attachment;
                    this.urlInUse = true;
                }
            }
        }
        return URL;
    }

    get initialAudio(): PostAttachment | null {
        let audio: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'audio') {
                    audio = attachment;
                    this.audioInUse = true;
                }
            }
        }
        return audio;
    }

    get initialImages(): ImageFile[] | null {
        if (this.initialImagesPost !== this.editPost) {
            this.initialImagesPost = this.editPost;
            const images = [...(this.editPost?.images ?? []), ...(this.editPost?.attachments ?? []).filter(a => a.attachment_type === 'image')];
            const unique = images.filter((image, index) => images.findIndex(other => other.id === image.id) === index);
            this.cachedInitialImages = unique.length ? unique as ImageFile[] : null;
        }
        return this.cachedInitialImages;
    }

    get initialSurveyPoll(): PostAttachment | Poll | undefined {
        let survey: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (attachment.attachment_type == 'survey_url') {
                    this.surveyPollInUse = true;
                    survey = attachment;
                }
            }
        }
        return survey ?? this.editPoll;
    }

    get initialAttachment(): PostAttachment | Heatmap | Poll | undefined {
        let initial: PostAttachment | null = null
        if (this.editPost?.attachments) {
            for (let attachment of this.editPost?.attachments) {
                if (this.defaultAttachments.includes(attachment.attachment_type) || attachment.attachment_type == 'video_url' || attachment.attachment_type == 'gif_url') {
                    initial = attachment;
                }
            }
        }
        if (initial ?? this.editPost?.heatmap_surveys?.[0] ?? this.editPoll) {
            this.multiAttachmentInUse = true;
        }
        return initial ?? this.editPost?.heatmap_surveys?.[0] ?? this.editPoll;
    }

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    /** Return whether the current user may change an edited post's controls. */
    get canModerateEditedPost(): boolean {
        if (!this.editPost) return true;
        return this.authService.isStaff() || this.isSuperuser || (
            this.community?.id === this.editPost.community &&
            ['moderator', 'full_admin'].includes(this.community?.user_membership?.access_level ?? '')
        );
    }

    get postVoteButtonText(): string {
        const postText = this.canRenamePostButtons ? this.createPostForm.get('vote_button_text')?.value
            : (this.editPost?.effective_vote_button_text || this.editPost?.vote_button_text);
        return postText?.trim()
            || this.community?.vote_button_text?.trim() || 'Vote';
    }

    get postAttachmentButtonText(): string {
        const postText = this.canRenamePostButtons ? this.createPostForm.get('attachment_button_text')?.value
            : (this.editPost?.effective_attachment_button_text || this.editPost?.attachment_button_text);
        return postText?.trim()
            || this.community?.attachment_button_text?.trim() || 'Attachment';
    }

    get canRenamePostButtons(): boolean {
        return this.community?.allow_post_button_text_customisation === true
            && this.canCustomisePostButtonText;
    }

    get canRenamePostLinkButton(): boolean {
        return this.community?.allow_post_link_button_text_customisation === true
            && this.canCustomisePostButtonText;
    }

    private get canCustomisePostButtonText(): boolean {
        return this.community?.id === this.communityId && (
            this.authService.isStaff() || this.isSuperuser
            || ['moderator', 'full_admin'].includes(this.community?.user_membership?.access_level ?? '')
        );
    }

    private postFormPayload(): NewPost {
        const fields = removeNullUndefinedFields<NewPost>(this.createPostForm.value, true, ['vote_button_text', 'attachment_button_text', 'link_button_text']);
        if (!this.canRenamePostButtons) {
            delete fields.vote_button_text;
            delete fields.attachment_button_text;
        }
        if (!this.canRenamePostLinkButton) {
            delete fields.link_button_text;
        }
        return fields;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: Event): void {
        event.preventDefault();
        event.returnValue = true;
    }

    public onReady(editor: any): void {
        this.clearEditorStartupTimer();
        this.editorReady = true;
        this.editorInstance = editor
        // A late startup must not replace the writing surface while the user is typing.
        if (this.destroyed || this.useBasicEditor) return;

        editor.plugins.get(FileRepository).createUploadAdapter = (loader: any) => {
            return new Base64CompressUploadAdapter(loader, this.compressImage);
        }

        // move the pop ups in the same area as the ckedtor so they are visible
        const hostEl: HTMLElement | undefined = this.ckHostRef?.nativeElement;
        const popoverRoot = hostEl?.closest('[popover]') as HTMLElement | null;
        if (!popoverRoot) return;

        const moveWrapperIntoPopover = (): void => {
            const wrapper = document.querySelector('.ck-body-wrapper') as HTMLElement | null;
            if (wrapper && wrapper.parentElement !== popoverRoot) {
                popoverRoot.appendChild(wrapper);
            }
        };

        moveWrapperIntoPopover();

        // keep it there if CK recreates it
        this.ckUiObserver?.disconnect();
        this.ckUiObserver = new MutationObserver(moveWrapperIntoPopover);
        this.ckUiObserver.observe(document.body, {childList: true, subtree: true});
    }

    onEditorError(error: unknown): void {
        if (this.destroyed || this.useBasicEditor) return;
        console.error('Discussion editor unavailable; using the basic editor.', error);
        this.clearEditorStartupTimer();
        this.basicEditorHtml = this.textFormControl.value ?? '';
        this.useBasicEditor = true;
        this.ckUiObserver?.disconnect();
    }

    updateBasicEditor(element: HTMLElement): void {
        const hasContent = !!element.textContent?.trim() || !!element.querySelector('img, table, hr');
        const html = hasContent ? this.sanitizer.sanitize(SecurityContext.HTML, element.innerHTML) ?? '' : '';
        this.textFormControl.setValue(html);
        this.textFormControl.markAsDirty();
    }

    pasteBasicEditor(event: ClipboardEvent, element: HTMLElement): void {
        // Insert clipboard text as DOM nodes so pasted markup cannot run in the fallback.
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain');
        if (!text) return;
        const selection = window.getSelection();
        if (!selection) return;
        const range = selection.rangeCount ? selection.getRangeAt(0) : document.createRange();
        if (!element.contains(range.commonAncestorContainer)) {
            range.selectNodeContents(element);
            range.collapse(false);
        }
        range.deleteContents();
        const fragment = document.createDocumentFragment();
        text.split(/\r\n|\r|\n/).forEach((line, index) => {
            if (index) fragment.appendChild(document.createElement('br'));
            fragment.appendChild(document.createTextNode(line));
        });
        const lastNode = fragment.lastChild!;
        range.insertNode(fragment);
        range.setStartAfter(lastNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        this.updateBasicEditor(element);
    }

    private clearEditorStartupTimer(): void {
        clearTimeout(this.editorStartupTimer);
        this.editorStartupTimer = undefined;
    }

    ngOnDestroy(): void {
        this.unregisterNativeBack();
        this.destroyed = true;
        this.clearEditorStartupTimer();
        this.ckUiObserver?.disconnect();
        for (const item of this.uploads.values()) void item.handle.abort();
        this.uploads.clear();
        this.stopKeepAlive();
    }

    attachmentsValidEmitter(event: boolean): void {
        this.attachmentsValid = event
    }

    pollSurveyValidEmitter(event: boolean): void {
        this.pollSurveyValid = event
    }

    audioValidEmitter(event: boolean): void {
        this.audioValid = event
    }

    docValidEmitter(event: boolean): void {
        this.docValid = event
    }

    urlValidEmitter(event: boolean): void {
        this.urlValid = event
    }

    imageValidEmitter(event: boolean): void {
        this.imageValid = event
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    getAttachmentField(event: any): void {
        this.selectAttachment('media', event);
        this.attachmentType = event.type;
        this.attachment = event.value;
        this.attachmentInitial = event.initial;
        if (this.attachmentType == 'text') {
            this.attachmentsValid = true;
        }
    }

    setSurveyPoll(event: any): void {
        this.selectAttachment('survey', event);
        if (event.type == 'survey_url' || event.type == 'survey') {
            this.survey = event.value
            this.poll = null
        } else if (event.type == 'poll' && event.value) {
            this.poll = event.value
            for (let question of this.poll?.questions) {
                // for some reason display_condition_options_identifiers gets turned into an object at some point so turn back into array
                if (question.display_condition_options_identifiers) {
                    question.display_condition_options_identifiers = question.display_condition_options_identifiers.map((condition: any) =>
                        typeof condition === 'object' ? Object.values(condition).join('') : condition
                    );
                }
            }
            this.survey = null
        } else {
            this.poll = null
            this.survey = null
            this.pollSurveyValid = true;
        }
    }

    setDocument(event: any): void {
        this.selectAttachment('document', event);
        if (event.type == 'document') {
            this.document = event.value;
        } else {
            this.document = null;
            this.docValid = true;
        }
    }

    setUrl(event: any): void {
        this.selectAttachment('url', event);
        if (event.type == 'url') {
            this.url = event.value;
        } else {
            this.url = null;
            this.urlValid = true;
        }
    }

    setAudio(event: any): void {
        this.selectAttachment('audio', event);
        if (event.type == 'audio') {
            this.audio = event.value;
        } else {
            this.audio = null;
            this.audioValid = true;
        }
    }

    setImages(event: any): void {
        this.imagesChanged = !event.initial;
        this.retainedImageIds = event.retainedImageIds ?? [];
        const files: File[] = event.type === 'image' ? (Array.isArray(event.value) ? event.value : event.value ? [event.value] : []) : [];
        for (const slot of [...this.selection.keys()]) {
            if (slot.startsWith('image:') && Number(slot.split(':')[1]) >= files.length) {
                this.selectAttachment(slot, {type: 'text', value: null});
                this.selection.delete(slot);
            }
        }
        files.forEach((file, index) => this.selectAttachment(`image:${index}`, {type: 'image', value: file, initial: event.initial}));
        if (event.type == 'image') {
            this.images = event.value;
        } else {
            this.images = null;
            this.imageValid = true;
        }
    }

    async createPost(): Promise<void> {
        if (this.sending) return;
        this.sending = true
        let postData: NewPost;
        let type: PostAttachmentType | 'text';

        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';

                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }

        postData = this.addAttachmentToPayload(type) as NewPost;
        postData = {...postData, emoji_enabled: true};

        // @ts-ignore
        if (postData.categories && postData.categories?.length <= 0 || postData.categories == 0) {
            delete postData['categories'];
        }
        if (this.currentSubgroups?.length > 0) {
            postData['subgroups'] = []
            for (let subgroup of this.currentSubgroups) {
                postData['subgroups']?.push(subgroup.id)
            }
        } else {
            delete postData['subgroups'];
        }
        if (postData.lock_post_at) {
            postData.lock_post_at = new Date(postData.lock_post_at).toISOString();
        }

        if (postData.is_hidden && this.revealDateControl.value && this.revealTimeControl.value) {
            const date: Date = mergeDateAndTime(this.revealDateControl.value, this.revealTimeControl.value);
            postData.reveal_post_at = new Date(date).toISOString();
        }
        if (postData.is_closed_loop) {
            postData['original_post'] = this.postSearchControl?.value?.id;
        } else {
            delete postData['original_post'];
        }


        if (this.survey) {
            postData = {
                ...postData,
                survey_urls: this.survey
            }
        } else if (this.selection.has('survey') || !this.editPost?.attachments?.some(a => a.attachment_type === 'survey_url')) {
            postData.iframe_survey = false;
        }


        if (this.url) {
            postData = {
                ...postData,
                urls: this.url
            }
        }
        if (this.images) {
            postData.images = this.images
        }

        if (!await this.prepareAttachments(postData)) return;

        this.contentService.newPost(postData, this.onPostUploadProgress)
            .subscribe((res): void => {
                this.savingPost = true;
                this.uploads.clear();
                this.stopKeepAlive()
                if (this.poll) {
                    this.poll!.post = res.id;
                    this.pollsService.createNewPoll(removeNullUndefinedFields(this.poll!, true)).subscribe(r => {
                        if (type === 'heatmap') {
                            this.sendHeatMap(res.id)
                        } else {
                            this.postSuccess(res.id);
                        }
                    }, err => {
                        this.sending = false;
                        const error: string = err?.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                        this.snackbarService.openErrorSnackBar(error);
                    })
                } else if (type === 'heatmap') {
                    this.sendHeatMap(res.id)
                } else {
                    this.postSuccess(res.id);
                }
            }, err => {
                this.stopKeepAlive()
                this.sending = false;
                const error: string = err?.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            });
    }

    sendHeatMap(id: number): void {
        this.attachment!.post = id;
        // An existing post can receive a new heatmap that has no survey ID yet.
        if (!this.attachment!.id) {
            this.pollsService.createNewHeatmap(removeNullUndefinedFields(this.attachment!, true)).subscribe(r => {
                this.postSuccess();
            }, err => {
                this.sending = false;
                const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            })
        } else {
            this.pollsService.editHeatmap(removeNullUndefinedFields(this.attachment!, true)).subscribe(r => {
                this.postSuccess();
            }, err => {
                this.sending = false;
                const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            })
        }
    }


    postSuccess(id?: number): void {
        this.snackbarService.openSuccessSnackBar('Post has been successfully created.');
        this.sending = false;
        this.matDialog.close(true);
        if (id) {
            let subgroup: string | null = this.currentRouteSubgroup;

            if (subgroup) {
                // go back to subgroup they started in
                this.router.navigate(['/feed', id], {queryParams: {subgroup}});
            } else {
                this.router.navigate(['/feed', id]);
            }
        }
    }

    update(): void {
        if (this.attachmentComponent?.commentType === 'poll' && this.attachmentComponent?.isEditPoll) {
            this.attachmentComponent?.createPollComponent?.updatePoll();
            this.updatedPoll.pipe(take(1)).subscribe(() => this.updatePost());
        } else {
            this.updatePost();
        }
    }

    async updatePost(): Promise<void> {
        if (this.sending) return;
        this.sending = true;
        let postData: UpdatePost;
        let type: PostAttachmentType | 'text';
        let remove_subgroups = null;
        let unlock_post = null;
        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';

                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }
        postData = this.postFormPayload();
        if (postData.is_hidden && this.revealDateControl.value && this.revealTimeControl.value) {
            const date: Date = mergeDateAndTime(this.revealDateControl.value, this.revealTimeControl.value);
            postData.reveal_post_at = new Date(date).toISOString();
        }

        if (this.survey) {
            postData = {
                ...postData,
                survey_urls: this.survey
            }
        } else if (this.selection.has('survey') || !this.editPost?.attachments?.some(a => a.attachment_type === 'survey_url')) {
            postData.iframe_survey = false;
        }


        if (this.url) {
            postData = {
                ...postData,
                urls: this.url
            }
        }

        if (this.images) {
            postData.images = this.images
        }


        if ((postData as any)['polls']) {
            delete (postData as any)['polls'];
        }
        // @ts-ignore
        if (postData.categories && postData.categories?.length <= 0 || postData.categories == 0) {
            postData['categories'] = [];
        }

        if (this.currentSubgroups?.length > 0) {
            postData['subgroups'] = []
            for (let subgroup of this.currentSubgroups) {
                postData['subgroups']?.push(subgroup.id)
            }
        } else {
            delete postData['subgroups']
            remove_subgroups = true;
        }

        if (postData.lock_post_at) {
            postData.lock_post_at = new Date(postData.lock_post_at).toISOString();
        } else {
            unlock_post = true;
        }

        if (postData.is_closed_loop) {
            postData['original_post'] = this.postSearchControl?.value?.id;
        } else {
            delete postData['original_post'];
        }

        postData.moderated = false;

        if (postData.attachment_files) {
            postData.attachment_files = postData.attachment_files.filter(function (el): boolean {
                return el != null;
            });
        }

        if (!postData.attachment_files) {
            delete postData['attachment_files'];
        }

        if (!await this.prepareAttachments(postData)) return;
        if (!this.canModerateEditedPost) {
            // Ordinary authors can edit their content without resubmitting
            // moderation and audience fields from the shared editor form.
            for (const field of [
                'community', 'subgroups', 'moderated', 'hidden', 'is_hidden',
                'deleted', 'is_pinned', 'is_pinned_to_sidebar', 'reveal_post_at',
                'hide_unmoderated_comments', 'comments_off', 'likes_off',
                'dislikes_off', 'lock_post_at', 'off_comments_at_time',
                'off_likes_at_time', 'off_dislikes_at_time'
            ]) {
                delete (postData as Record<string, unknown>)[field];
            }
            remove_subgroups = null;
            unlock_post = null;
        }
        this.contentService.updatePost(this.editPost!.id, postData, false, {remove_subgroups, unlock_post}, this.onPostUploadProgress)
            .subscribe((res): void => {
                this.savingPost = true;
                this.uploads.clear();
                this.stopKeepAlive()
                if (this.poll) {
                    this.poll!.post = res.id;
                    this.pollsService.createNewPoll(removeNullUndefinedFields(this.poll!, true)).subscribe(r => {
                        if (type === 'heatmap') {
                            this.sendHeatMap(res.id)
                        } else {
                            this.postSuccess();
                        }
                    }, err => {
                        this.sending = false;
                        const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                        this.snackbarService.openErrorSnackBar(error);
                    })
                } else if (type === 'heatmap') {
                    this.sendHeatMap(res.id)
                } else {
                    this.postSuccess();
                }
            }, err => {
                this.stopKeepAlive()
                this.sending = false;
                const error: string = err.includes('0 Unknown Error') ? 'Your file is too large, please try uploading a smaller one' : err;
                this.snackbarService.openErrorSnackBar(error);
            });
    }

    clearDate(): void {
        this.dateControl?.reset(null);
    }

    clearRevealDate(): void {
        this.revealDateControl?.reset(null);
    }

    selectSubgroup(event: MatAutocompleteSelectedEvent): void {
        const subgroup = event as any;
        this.currentSubgroups.push(subgroup);
        this.subgroupSearchControl.patchValue('')
    }

    removeSubgroup(metric: any): void {
        const i: number = this.currentSubgroups.findIndex((x: any): boolean => x === metric);
        if (i != null && i >= 0) {
            this.currentSubgroups.splice(i, 1);
        }
    }

    displayFn(subgroup: any): string {
        return subgroup && subgroup.name ? subgroup.name : '';
    }

    postDisplayFn(post: FeedPost): string {
        return post && post.title ? post.title : '';
    }

    checkIfInArray(item: any, otherArray: any): boolean {
        let i;
        if (otherArray) {
            i = otherArray.findIndex((x: any): boolean => x === item);
        }
        return (i != null && i >= 0);
    }

    setUse(event: any, value: any): void {
        switch (value) {
            case 'docInUse':
                this.docInUse = event;
                break;
            case 'urlInUse':
                this.urlInUse = event;
                break;
            case 'imageInUse':
                this.imageInUse = event;
                break;
            case 'surveyPollInUse':
                this.surveyPollInUse = event;
                break;
            case 'multiAttachmentInUse':
                this.multiAttachmentInUse = event;
                break;
            case 'audioInUse':
                this.audioInUse = event;
                break;
        }
    }

    validateForm(formGroup: FormGroup | FormArray, parentKey: string = ''): string {
        let errors: string = ''
        const titleCasePipe = new TitleCasePipe();

        Object.keys(formGroup.controls).forEach(key => {
            const control: AbstractControl<any, any> | null = formGroup.get(key);
            const controlPath: string = parentKey ? `${parentKey}.${key}` : key;


            if (control instanceof FormGroup || control instanceof FormArray) {
                this.validateForm(control, controlPath);  // Recursive call
            } else if (control && control.invalid) {
                errors = errors + `Invalid field: ` + titleCasePipe.transform(controlPath) + `, `
            }
        });

        if (!this.docValid) {
            errors = errors + `Document invalid, `
        }
        if (!this.urlValid) {
            errors = errors + `Url invalid, `
        }
        if (!this.pollSurveyValid) {
            errors = errors + `Poll/Survey form invalid, `
        }
        if (!this.attachmentsValid) {
            errors = errors + `Media attachment invalid, `
        }
        return errors
    }

    exitCheck(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'appFixWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to exit? Changes that you have made will not be saved.',
                title: 'Exit',
                showHeader: true,
            }
        });
        dialogRef.afterClosed().subscribe(r => {
            if (r) {
                this.matDialog.close()
            }
        });
    }

    private compressImage = async (file: File): Promise<File | Blob> => {
        const imageCompression = (await import('browser-image-compression')).default;
        try {
            let compressedFile = await imageCompression(file, {
                maxWidthOrHeight: 1500,
                useWebWorker: true,
                fileType: 'image/jpeg'
            });
            compressedFile = new File([compressedFile], "compressed.jpeg", {type: 'image/jpeg'});
            const savedKB: number = (file.size - compressedFile.size) / 1024;
            console.log(`Kb Saved: ${savedKB} (${(savedKB / (file.size / 1024)) * 100}%)`);
            return compressedFile;
        } catch (error) {
            console.error(error);
            return file;
        }

    };

    private addAttachmentToPayload(type: PostAttachmentType | 'text'): NewPost | UpdatePost {
        const fields = this.postFormPayload();
        if (type === 'video_url') {
            return {
                ...fields,
                video_urls: this.attachment
            };
        } else if (type === 'gif_url') {
            return {
                ...fields,
                gif_urls: this.attachment?.embed_url ?? this.attachment
            };
        } else if (type !== 'text' && type !== 'poll' && type !== 'heatmap') {
            return {
                ...fields,
                attachment_files: [this.attachment]
            };
        } else {
            return fields;
        }
    }

    private loadSubgroups(communityId: number): void {
        this.createPostForm.get('subgroup')?.reset(null);
        if (!communityId) return;
        this.communitiesService.getSubGroups(communityId).pipe(take(1))
            .subscribe(subgroups => {
                for (let subgroup of subgroups) {
                    this.subgroups.push(subgroup);
                }
                if (!this.subgroups.length) {
                    this.createPostForm.get('subgroup')?.disable();
                } else {
                    if (!this.data?.post) {
                        let currentSubgroup = this.currentRouteSubgroup
                        if (currentSubgroup) {
                            for (let subgroup of this.subgroups) {
                                if (currentSubgroup == String(subgroup.id)) {
                                    this.currentSubgroups.push(subgroup)
                                }
                            }
                        }
                    } else if (this.data?.post?.subgroups) {
                        for (let subgroup of this.subgroups) {
                            // fill current subgroups if edit post has subgroups
                            for (let currentsubgroup of this.data?.post?.subgroups) {
                                if (currentsubgroup.id == subgroup.id) {
                                    this.currentSubgroups.push(subgroup)
                                }
                            }
                        }
                    }
                }
            });


    }

    private loadDataForEdit(post: Post): void {
        this.createPostForm.patchValue(post);
        this.lockPostControl.patchValue(!!post.lock_post_at);
        if (post.original_post) {
            this.postSearchControl.setValue(post.original_post);
        }
        if (post.lock_post_at) {
            const {date, time} = parseISODate(post.lock_post_at);
            this.dateControl.patchValue(date);
            this.timeControl.patchValue(time);
            this.lockPostControl.get('lock_post_at')?.patchValue(localDateShift(post.lock_post_at));
        }
        if (post.reveal_post_at) {
            const {date, time} = parseISODate(post.reveal_post_at);
            this.revealDateControl.patchValue(date);
            this.revealTimeControl.patchValue(time);
        }
    }

    private getCategories(communityId: number, post?: Post): void {
        this.createPostForm.get('categories')?.reset(null);
        this.createPostForm.get('categories')?.disable();
        if (!communityId) return;
        this.categories = [];
        this.createPostForm.get('categories')?.enable();
        this.communitiesService.getCategories(communityId).pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => {
                    this.categories = res;
                    if (!this.categories.length) {
                        this.createPostForm.get('categories')?.disable();
                    } else if (post?.categories) {
                        let categoryIds: number[] = []
                        for (let category of post.categories) {
                            let i = this.categories.findIndex((x: any): boolean => x.id === category.id);
                            if (i != null && i >= 0) {
                                // only add category if it hasn't been deleted
                                categoryIds.push(category.id)
                            }
                        }
                        this.createPostForm.get('categories')?.setValue(categoryIds);
                    } else {
                        this.createPostForm.get('categories')?.setValue(0)
                    }
                }, error => {
                    this.snackbarService.openErrorSnackBar(error);
                    this.createPostForm.get('categories')?.setValue(0)
                }
            );
    }

    private changeCommunity(communityId: number): void {
        if (!communityId) return;
        const community: Community = this.communities.find(community => community.id === communityId)!
        this.emojiEnabled = community?.emoji_enabled;
        this.communitiesService.setCurrentDashboardCommunity(community);
    }

    private get currentRouteSubgroup(): string | null {
        return this.data?.subgroup != null
            ? String(this.data.subgroup)
            : this.route.snapshot.queryParamMap.get('subgroup');
    }

    private manageLockState(): void {
        const lock$: Observable<any> = this.lockPostControl.valueChanges;
        lock$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res): void => {
                if (res) {
                    this.dateControl.enable();
                    this.timeControl.enable();
                    this.createPostForm.get('lock_post_at')?.enable();
                    this.createPostForm.get('off_comments_at_time')?.enable();
                    this.createPostForm.get('off_likes_at_time')?.enable();
                    this.createPostForm.get('off_dislikes_at_time')?.enable();
                    this.createPostForm.get('lock_post_at')?.addValidators(Validators.required);
                    this.createPostForm.get('lock_post_at')!.updateValueAndValidity();
                } else {
                    this.dateControl.disable();
                    this.timeControl.disable();
                    this.createPostForm.get('lock_post_at')?.reset();
                    this.createPostForm.get('lock_post_at')?.disable();
                    this.createPostForm.get('off_comments_at_time')?.reset();
                    this.createPostForm.get('off_comments_at_time')?.disable();
                    this.createPostForm.get('off_likes_at_time')?.reset();
                    this.createPostForm.get('off_likes_at_time')?.disable();
                    this.createPostForm.get('off_dislikes_at_time')?.reset();
                    this.createPostForm.get('off_dislikes_at_time')?.disable();
                    this.createPostForm.get('lock_post_at')?.removeValidators(Validators.required);
                    this.createPostForm.get('lock_post_at')!.updateValueAndValidity();
                }
            });

        const time$: Observable<any> = this.timeControl.valueChanges;
        const date$: Observable<any> = this.dateControl.valueChanges;

        combineLatest([date$, time$, lock$])
            .pipe(
                distinctUntilChanged(this.customComparator),
                filter(res => !!res[2]),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(res => {
                const date: Date = mergeDateAndTime(res[0], res[1]);
                this.createPostForm.get('lock_post_at')?.patchValue(date);
                this.createPostForm.get('lock_post_at')?.updateValueAndValidity();
            })
    }

    private customComparator(
        prev: [Date, string, boolean],
        curr: [Date, string, boolean]
    ): boolean {
        return prev[0] === curr[0] && prev[1] === curr[1] && prev[2] === curr[2];
    }

    private _filterSubgroups(value: string): string[] {
        let filterValue: string = '';
        if (typeof value == 'string') {
            filterValue = value.toLowerCase();
        }
        let results: any[] = this.subgroups.filter((option: any) => option.name.toLowerCase().includes(filterValue));
        results = results.filter((item: any, index: any): boolean => {
            return (results.indexOf(item) == index);
        });
        return results;
    }
}
